import { useApiQuery } from '../useApiQuery';
import { apiGet, operationsClient } from '@/lib/api/client';
import { paymentKeys } from '@/lib/queryKeys';

/**
 * One row of a payment's refund history, as returned by
 * `GET /payments/:id/refunds`.
 *
 * `status` is deliberately a plain string: the server owns the vocabulary
 * (PENDING | COMPLETED | FAILED | NEEDS_REVIEW) and adding a value there must
 * not require a frontend release to stop rendering the list.
 */
export interface PaymentRefundHistoryRecord {
  id: string;
  paymentId: string | null;
  intent: string;
  amount: number;
  status: string;
  reason: string | null;
  failureReason: string | null;
  /**
   * Only on a NEEDS_REVIEW row: why the refund reconciler could not settle this
   * reservation from the processor's ledger, and what that ledger did report.
   * This is what the operator takes to the processor's own dashboard.
   */
  reviewReason: string | null;
  gatewayProvider: string | null;
  gatewayRefundId: string | null;
  processedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Refund history for one payment — reservations included, not just settled ones. */
export function usePaymentRefunds(paymentId: string | undefined) {
  return useApiQuery<PaymentRefundHistoryRecord[]>({
    queryKey: paymentKeys.refunds(paymentId),
    enabled: Boolean(paymentId),
    queryFn: () =>
      apiGet<PaymentRefundHistoryRecord[]>(operationsClient, `/payments/${paymentId}/refunds`),
    showErrorToast: false,
  });
}
